import React, { useState, useEffect, useMemo } from 'react';
import api from '../api/client';
import { Button, EmptyState, ConfirmModal } from '../components/common';
import { MeetingModal } from '../components/modals';
import { formatDate } from '../utils/formatters';

/**
 * Meetings Management page - Admin/Manager CRUD for meetings (tabular layout)
 */
const MeetingsManagement = ({ user }) => {
  const [meetings, setMeetings] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editMeeting, setEditMeeting] = useState(null);
  const [confirmState, setConfirmState] = useState(null);

  // Filter / sort state
  const [searchText, setSearchText] = useState('');
  const [attendeeFilter, setAttendeeFilter] = useState('all');
  const [sortColumn, setSortColumn] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [meetingsData, usersData] = await Promise.all([
        api.get('/meetings/all'),
        api.get('/users/list')
      ]);
      setMeetings(meetingsData);
      setUsers(usersData);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (meetingId) => {
    setConfirmState({
      message: 'Delete this meeting? This cannot be undone.',
      confirmLabel: 'Delete Meeting',
      onConfirm: async () => {
        setConfirmState(null);
        try {
          await api.delete(`/meetings/${meetingId}`);
          loadData();
        } catch (error) {
          console.error('Failed to delete meeting:', error);
        }
      },
    });
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const sortIndicator = (column) => {
    if (sortColumn !== column) return null;
    return sortDirection === 'asc' ? ' ↑' : ' ↓';
  };

  const filteredMeetings = useMemo(() => {
    let result = [...meetings];

    if (searchText.trim()) {
      const lower = searchText.toLowerCase();
      result = result.filter(m => m.name.toLowerCase().includes(lower));
    }

    if (attendeeFilter !== 'all') {
      const userId = parseInt(attendeeFilter, 10);
      result = result.filter(m => (m.participants || []).includes(userId));
    }

    result.sort((a, b) => {
      let aVal, bVal;
      if (sortColumn === 'name') {
        aVal = (a.name || '').toLowerCase();
        bVal = (b.name || '').toLowerCase();
      } else if (sortColumn === 'creatorName') {
        aVal = (a.creatorName || '').toLowerCase();
        bVal = (b.creatorName || '').toLowerCase();
      } else if (sortColumn === 'members') {
        aVal = (a.participants || []).length;
        bVal = (b.participants || []).length;
      } else if (sortColumn === 'createdAt') {
        aVal = new Date(a.createdAt);
        bVal = new Date(b.createdAt);
      }
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [meetings, searchText, attendeeFilter, sortColumn, sortDirection]);

  const hasActiveFilters = searchText.trim() || attendeeFilter !== 'all';

  if (loading) {
    return (
      <div className="page-header">
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Meetings Management</h1>
        <Button
          variant="primary"
          onClick={() => {
            setEditMeeting(null);
            setShowModal(true);
          }}
        >
          Create Meeting
        </Button>
      </div>

      {/* Toolbar */}
      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          type="text"
          className="form-input"
          placeholder="Search by meeting name..."
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          style={{ flex: '1', minWidth: '180px', maxWidth: '320px', width: 'auto' }}
        />
        <select
          className="form-input"
          value={attendeeFilter}
          onChange={e => setAttendeeFilter(e.target.value)}
          style={{ minWidth: '180px', width: 'auto' }}
        >
          <option value="all">All Attendees</option>
          {users.map(u => (
            <option key={u.id} value={u.id}>
              {u.firstName} {u.lastName}
            </option>
          ))}
        </select>
        {hasActiveFilters && (
          <Button
            variant="secondary"
            size="small"
            onClick={() => { setSearchText(''); setAttendeeFilter('all'); }}
          >
            Clear Filters
          </Button>
        )}
      </div>

      {meetings.length === 0 ? (
        <EmptyState
          icon="📅"
          title="No meetings"
          message="Create your first meeting to get started."
        />
      ) : filteredMeetings.length === 0 ? (
        <EmptyState
          icon="🔍"
          title="No meetings match your filters"
          message="Try adjusting your search or filter."
        />
      ) : (
        <>
          <table className="data-table">
            <thead>
              <tr>
                <th
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => handleSort('name')}
                >
                  Meeting Name{sortIndicator('name')}
                </th>
                <th
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => handleSort('creatorName')}
                >
                  Created By{sortIndicator('creatorName')}
                </th>
                <th
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => handleSort('members')}
                >
                  Members{sortIndicator('members')}
                </th>
                <th
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => handleSort('createdAt')}
                >
                  Created{sortIndicator('createdAt')}
                </th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredMeetings.map(meeting => (
                <tr key={meeting.id}>
                  <td>{meeting.name}</td>
                  <td>{meeting.creatorName || '—'}</td>
                  <td>{(meeting.participants || []).length}</td>
                  <td>{formatDate(meeting.createdAt)}</td>
                  <td style={{ textAlign: 'right' }}>
                    <div className="flex gap-1" style={{ justifyContent: 'flex-end' }}>
                      <Button
                        variant="secondary"
                        size="small"
                        onClick={() => {
                          setEditMeeting(meeting);
                          setShowModal(true);
                        }}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="secondary"
                        size="small"
                        style={{ color: 'var(--color-danger)' }}
                        onClick={() => handleDelete(meeting.id)}
                      >
                        Delete
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p style={{ marginTop: '0.75rem', fontSize: '0.875rem', color: 'var(--color-text-muted)' }}>
            Showing {filteredMeetings.length} of {meetings.length} meeting{meetings.length !== 1 ? 's' : ''}
          </p>
        </>
      )}

      {showModal && (
        <MeetingModal
          meeting={editMeeting}
          users={users}
          onClose={() => setShowModal(false)}
          onSave={() => {
            setShowModal(false);
            loadData();
          }}
        />
      )}

      {confirmState && (
        <ConfirmModal
          message={confirmState.message}
          confirmLabel={confirmState.confirmLabel}
          onConfirm={confirmState.onConfirm}
          onCancel={() => setConfirmState(null)}
        />
      )}
    </div>
  );
};

export default MeetingsManagement;
